import { arrayOf, bool, func, node, number, object, oneOfType, shape, string } from 'prop-types';
import React, { useRef } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { DEFAULT_FONT_SIZE } from '../../utilities/constants';
import { sourcesShapeTypescript } from '../../utilities/types';
import Dropdown, { DropdownProps } from '../Dropdown';
import Footer from '../Footer';
import { customFootnoteType, customFootnoteTypeTypescript } from '../Footnotes/types';
import { sourcesType } from '../Sources/types';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import styles from './styles.module.css';

export const DEFAULT_TITLE_FONT_SIZE = 20;
export const DEFAULT_SUBTITLE_FONT_SIZE = 16;

function renderSlot(el, args = []) {
	if (!el) {
		return null;
	}

	if (typeof el === 'function') {
		return el(...args);
	}

	return el;
}

type AnyFunc =  (...props: any[]) => any;
export type chartWrapperPropTypesTypescript = {
	children: Element | AnyFunc;
	/** Legend Slot */
	slotLegend?: Element | AnyFunc;
	/** Legend Slot */
	slotAboveChart?: Element | AnyFunc;
	/** Legend Slot */
	slotBelowChart?: Element | AnyFunc;
	/** Header/Footer Options */
	/** Chart Title */
	title?: string;
	/** Chart Subtitle */
	subtitle?: string;
	/** Title font size */
	titleFontSize?: number;
	/** Subtitle font size */
	subtitleFontSize?: number;
	/** Footer sources */
	footerSources?: sourcesShapeTypescript[];
	/** footer summary */
	footerSummary?: string,
	/** Footer footnotes */
	footnotes?: customFootnoteTypeTypescript[];
	/** Shows the footer */
	showFooter?: boolean;
	/** Disables footnotes from metrics */
	/** Styling */
	/** Width to draw the chart. Will fill container if unset. */
	width?: number;
	/** Height to draw the chart */
	height?: number | string;
	/** sets default font size for chart body elements and footer */
	fontSize?: number;
	/** Chart classname */
	className?: string;
	/** Custom styles for chart wrapper */
	style?: Record<any, any>;
	/** Dropdown in header */
	dropdowns?: DropdownProps[];
	/** Events */
	/** On chart mouseout */
	onMouseLeave?: AnyFunc;
};

const ChartWrapper = ({
	/** Req Data */
	children,
	slotAboveChart = undefined,
	slotBelowChart = undefined,
	/** Header/Footer Options */
	title = '',
	subtitle = '',
	titleFontSize = DEFAULT_TITLE_FONT_SIZE,
	subtitleFontSize = DEFAULT_SUBTITLE_FONT_SIZE,
	footerSources = [],
	footerSummary,
	footnotes = [],
	showFooter = true,
	/** Styling */
	width,
	height = 'auto',
	fontSize = DEFAULT_FONT_SIZE,
	className = '',
	style = {},
	dropdowns = [],
	/** Events */
	onMouseLeave = () => { }
}: chartWrapperPropTypesTypescript) => {
	const ref = useRef();
	const { width: chartWidth = 0, height: chartHeight = 0 } = useResizeDetector({
		targetRef: ref,
		refreshMode: 'debounce',
		refreshRate: 500,
		refreshOptions: {
			leading: true,
			trailing: true
		}
	});

	return (
		<div
			className={`${styles['chart-wrapper']} ${className}`}
			style={{
				...style,
				width: width || null,
				minHeight: height || 0,
				fontSize
			}}
		>
			{(title || dropdowns.length !== 0) && (
				<div className={styles['chart-title']} style={{ fontSize: titleFontSize }}>
					{title && <span className="title-text">{title}</span>}
					{dropdowns.length !== 0 && (
						<span className={styles['dropdown-container']}>
							{dropdowns.map((dropdown, i) => (
								<Dropdown key={i} {...dropdown} />
							))}
						</span>
					)}
				</div>
			)}
			{subtitle && (
				<div className={styles['chart-subtitle']} style={{ fontSize: subtitleFontSize }}>
					{subtitle}
				</div>
			)}
			<div className={styles['chart-content']}>
				{slotAboveChart && renderSlot(slotAboveChart, [{ chartHeight, chartWidth }])}
				<div
					ref={ref}
					style={{
						display: 'flex',
						flex: 1,
						flexWrap: 'wrap'
					}}
					onMouseLeave={onMouseLeave}
					onBlur={() => { }}
				>
					{renderSlot(children, [{ chartHeight, chartWidth }])}
				</div>
				{slotBelowChart && renderSlot(slotBelowChart, [{ chartHeight, chartWidth }])}

			</div>
			{showFooter && (
				<Footer
					sources={footerSources}
					summary={footerSummary}
					footnotes={footnotes}
					fontSize={fontSize}
				/>
			)}
		</div>
		// eslint-disable-next-line react-hooks/exhaustive-deps
	);
}

const chartWrapperPropTypes = {
	/** Req Data */
	/** Child node */
	children: oneOfType([node, func]),
	/** Legend Slot */
	slotLegend: oneOfType([node, func]),
	/** Header/Footer Options */
	/** Chart Title */
	title: string,
	/** Chart Subtitle */
	subtitle: string,
	/** Title font size */
	titleFontSize: number,
	/** Subtitle font size */
	subtitleFontSize: number,
	/** Footer sources */
	footerSources: arrayOf(sourcesType),
	/** footer summary */
	footerSummary: string,
	/** Footer footnotes */
	footnotes: arrayOf(customFootnoteType),
	/** Shows the footer */
	showFooter: bool,
	/** Disables footnotes from metrics */
	/** Styling */
	/** Width to draw the chart. Will fill container if unset. */
	width: number,
	/** Height to draw the chart */
	height: oneOfType([number, string]),
	/** sets default font size for chart body elements and footer */
	fontSize: number,
	/** Chart classname */
	className: string,
	/** Custom styles for chart wrapper */
	style: object,
	/** Dropdown in header */
	dropdowns: arrayOf(
		shape({
			options: arrayOf(oneOfType([number, string])),
			color: string,
			label: string,
			value: oneOfType([string, number]),
			parser: func,
			onChange: func.isRequired
		})
	),
	/** Events */
	/** On chart mouseout */
	onMouseLeave: func
};

// eslint-disable-next-line no-restricted-exports
export { ChartWrapper as default, chartWrapperPropTypes };
